/** Ask ONE — floating chat panel.
 *  Ops asks natural-language questions about deployments, playbook,
 *  billing.  Answers cite real records. */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, X, Send, Loader2 } from "lucide-react";

const SUGGESTED = [
  "Which deployments need attention today?",
  "Which deployed devices are missing MDM verification?",
  "Which employees have not acknowledged delivery?",
  "Which deployments are ready for billing?",
  "What MDM does ABC Global use?",
];

export default function AskOneChat() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `askone-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (open && bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [msgs, open]);

  const ask = async (q) => {
    const question = (q ?? input).trim();
    if (!question) return;
    setMsgs((m) => [...m, { role: "user", text: question }]);
    setInput(""); setLoading(true);
    try {
      const { data } = await api.post("/ask-one/query", { question, session_id: sessionId });
      setMsgs((m) => [...m, {
        role: "one",
        text: data.answer,
        intent: data.intent,
        citations: data.citations || [],
        source: data.source,
        inherited: data.inherited_customer,
        action: data.action,
      }]);
    } catch (e) {
      toast.error("Ask ONE unavailable");
      setMsgs((m) => [...m, { role: "one", text: "Sorry — I couldn't answer that just now. Try again in a moment." }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-36 md:bottom-20 right-5 z-fab h-11 w-11 rounded-full shadow-lg grid place-items-center transition-transform hover:scale-105"
          style={{ background: "var(--st-accent)", color: "white" }}
          data-testid="ask-one-launcher"
          aria-label="Ask ONE"
        >
          <Sparkles className="h-5 w-5" strokeWidth={1.8} />
        </button>
      )}
      {open && (
        <div className="fixed bottom-20 right-5 z-panel w-[380px] max-w-[calc(100vw-2.5rem)] h-[520px] max-h-[80vh] flex flex-col rounded-lg shadow-2xl border border-[color:var(--st-border)] bg-[color:var(--st-surface)]"
             data-testid="ask-one-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--st-border)]">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" style={{ color: "var(--st-accent)" }} strokeWidth={1.8} />
              <div>
                <div className="text-sm font-semibold">Ask ONE</div>
                <div className="text-[10px] text-[color:var(--st-text-subtle)]">Answers cite real records</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ask-one-close"
              className="h-7 w-7 grid place-items-center rounded-md hover:bg-[color:var(--st-surface-alt)]">
              <X className="h-4 w-4" strokeWidth={1.6} />
            </button>
          </div>

          {/* Body */}
          <div ref={bodyRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="ask-one-body">
            {msgs.length === 0 && (
              <div className="space-y-2">
                <div className="text-[12px] text-[color:var(--st-text-muted)]">Try:</div>
                {SUGGESTED.map((s) => (
                  <button key={s} onClick={() => ask(s)} data-testid="ask-one-suggested"
                    className="w-full text-left text-[13px] px-3 py-2 rounded-md border border-[color:var(--st-border)] hover:bg-[color:var(--st-surface-alt)] transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={"max-w-[85%] rounded-lg px-3 py-2 text-[13px] " +
                  (m.role === "user"
                    ? "bg-[color:var(--st-text)] text-white"
                    : "bg-[color:var(--st-surface-alt)] text-[color:var(--st-text)]")
                }>
                  <div className="whitespace-pre-wrap">{m.text}</div>
                  {m.citations?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-black/10 space-y-1">
                      <div className="text-[10px] uppercase tracking-wider opacity-60">Sources</div>
                      {m.citations.slice(0, 5).map((c, ci) => (
                        <div key={ci} className="text-[11px] font-mono opacity-80" data-testid="ask-one-citation">
                          {c.kind}: {c.customer || c.employee || c.batch_id || c.id?.slice(0, 8) || ""}
                        </div>
                      ))}
                      {m.citations.length > 5 && (
                        <div className="text-[10px] opacity-60">+ {m.citations.length - 5} more</div>
                      )}
                    </div>
                  )}
                  {m.action?.url && (
                    <a href={m.action.url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${m.action.url}` : m.action.url} target={m.action.url.startsWith("/api") ? "_blank" : undefined} rel="noreferrer"
                      className="mt-2 inline-flex rounded-full bg-[color:var(--st-text)] px-3 py-1 text-[11px] font-semibold text-white" data-testid="ask-one-action">{m.action.label}</a>
                  )}
                  {m.inherited && (
                    <div className="mt-1 text-[10px] opacity-60">Using context: {m.inherited}</div>
                  )}
                  {m.source && (
                    <div className="mt-1 text-[9px] opacity-50">via {m.source}</div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-[12px] text-[color:var(--st-text-subtle)]">
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.6} /> ONE is thinking…
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={(e) => { e.preventDefault(); ask(); }}
                className="p-3 border-t border-[color:var(--st-border)] flex gap-2" data-testid="ask-one-form">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about deployments, playbook…"
              data-testid="ask-one-input"
              className="flex-1 px-3 py-2 text-[13px] rounded-md border border-[color:var(--st-border)] focus:outline-none focus:ring-1 focus:ring-[color:var(--st-accent)]"
            />
            <button type="submit" disabled={loading || !input.trim()} data-testid="ask-one-send"
              className="h-9 w-9 grid place-items-center rounded-md text-white disabled:opacity-40"
              style={{ background: "var(--st-accent)" }}>
              <Send className="h-4 w-4" strokeWidth={1.8} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
